import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
// Location uses native select (no react-select)
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaFileExport, 
  FaSearch,
  FaDownload,
  FaArrowRight,
  FaArrowLeft,
  FaBox,
  FaWarehouse,
  FaTruck,
  FaBook,
  FaTrashAlt
} from 'react-icons/fa';

export default function TilesDayBook() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [dayBookData, setDayBookData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalEntries: 0,
    entriesPerPage: 50
  });
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    location: '',
    page: 1,
    limit: 50,
    sortBy: 'date', // Set default to 'date'
    sortOrder: 'asc'
  });



  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Get company ID from user context
  const getCompanyId = () => {
      return user.user.company._id || user.user.company;
    return '';
  };

  useEffect(() => {
    fetchLocations();
    // Set default date range to last 3 days
    const now = new Date();
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(now.getDate() - 3);
    
    setFilters({
      dateFrom: threeDaysAgo.toISOString().split('T')[0],
      dateTo: now.toISOString().split('T')[0],
      location: null,
      page: 1,
      limit: 50,
      sortBy: 'date',
      sortOrder: 'asc'
    });
  }, []);

  // Auto-fetch data when filters change (except page changes)
  useEffect(() => {
    if (filters.dateFrom && filters.dateTo) {
      fetchDayBookData(filters.page);
    }
  }, [filters.dateFrom, filters.dateTo, filters.location, filters.limit, filters.sortBy, filters.sortOrder]);

  const fetchLocations = async () => {
    try {
      const companyId = getCompanyId();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/locations${companyId ? `/companyId/${companyId}` : ''}`,
        {
          credentials: 'include'
        }
      );
      const data = await response.json();
      const locations = Array.isArray(data) ? data : [];
      setLocations(locations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      Swal.fire('Error', 'Failed to fetch locations', 'error');
    }
  };

  const fetchDayBookData = async (pageNum = 1) => {
    if (!filters.dateFrom || !filters.dateTo) {
      Swal.fire('Error', 'Please select both date from and date to', 'error');
      return;
    }

    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        ...(filters.location && { location: filters.location }),
        ...(companyId && { companyId }),
        page: pageNum,
        limit: filters.limit,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        format: 'json'
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/tiles/daybook?${params}`,
        {
          credentials: 'include'
        }
      );

      const data = await response.json();
      if (data.success) {
        setDayBookData(data.data || []);
        setPagination(data.pagination || {});
      } else {
        throw new Error(data.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching daybook data:', error);
      Swal.fire('Error', 'Failed to fetch daybook data', 'error');
      setDayBookData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...(field !== 'page' && { page: 1 }) // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
    fetchDayBookData(newPage);
  };

  const handleSortChange = (sortBy) => {
    const newSortOrder = filters.sortBy === sortBy && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: newSortOrder,
      page: 1
    }));
  };

  const exportToExcel = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      Swal.fire('Error', 'Please select both date from and date to', 'error');
      return;
    }

    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        ...(filters.location && { location: filters.location }),
        ...(companyId && { companyId }),
        format: 'excel',
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/tiles/daybook?${params}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Tiles_DayBook_${filters.dateFrom}_to_${filters.dateTo}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        Swal.fire('Success', 'Excel file downloaded successfully', 'success');
      } else {
        throw new Error('Failed to download Excel file');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      Swal.fire('Error', 'Failed to download Excel file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (dayBookData.length === 0) {
      Swal.fire('Warning', 'No data to export', 'warning');
      return;
    }

    const headers = [
      'Date',
      'Tile Name',
      'Tile Code',
      'Location',
      'Supplier',
      'ID', // Changed from 'Remarks'
      'Inward FS Box',
      'Inward FS Pieces',
      'Inward DS Box',
      'Inward DS Pieces',
      'Outward FS Box',
      'Outward FS Pieces',
      'Outward DS Box',
      'Outward DS Pieces'
    ];

    let csvContent = headers.join(',') + '\n';

    dayBookData.forEach(entry => {
      const row = [
        entry.date ? formatDate(entry.date) : '',
        `"${entry.tileName || ''}"`,
        `"${entry.tileCode || ''}"`,
        `"${entry.location?.name || entry.location?.locationName || ''}"`,
        `"${entry.inward?.supplier || ''}"`,
        `"${entry.inward?.id || entry.outward?.id || ''}"`, // Changed from remarks
        entry.inward?.fsBox || 0,
        entry.inward?.fsPieces || 0,
        entry.inward?.dsBox || 0,
        entry.inward?.dsPieces || 0,
        entry.outward?.fsBox || 0,
        entry.outward?.fsPieces || 0,
        entry.outward?.dsBox || 0,
        entry.outward?.dsPieces || 0
      ];
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tiles_daybook_${filters.dateFrom}_to_${filters.dateTo}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const groupEntriesByDate = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const dateKey = new Date(entry.date).toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(entry);
    });
    
    return grouped;
  };



  const groupedData = groupEntriesByDate(dayBookData);

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="w-full mx-auto space-y-6">
        {/* Header Card */}
        <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl shadow-2xl p-8">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <FaBook className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">Tiles Day Book</h1>
                <p className="text-white/90">Daily tiles transactions report</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className={`p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2" />
                  Date From *
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className="inline mr-2" />
                  Date To *
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaMapMarkerAlt className="inline mr-2" />
                  Location
                </label>
                <select
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className={`w-full px-3 py-2.5 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                >
                  <option value="">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc._id} value={loc._id}>{loc.name || loc.locationName}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end gap-2">
                <button
                  onClick={() => fetchDayBookData(1)}
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaSearch />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Export and Pagination Controls */}
            {dayBookData.length > 0 && (
              <div className={`px-6 py-4 border-t mt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Export Options:</span>
                    <button
                      onClick={exportToCSV}
                      disabled={loading}
                      className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center gap-2 text-sm"
                    >
                      <FaDownload />
                      CSV
                    </button>
                    <button
                      onClick={exportToExcel}
                      disabled={loading}
                      className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center gap-2 text-sm"
                    >
                      <FaFileExport />
                      Excel
                    </button>
                  </div>
                  
                  {/* Pagination Controls */}
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Page {pagination.currentPage} of {pagination.totalPages} 
                        ({pagination.totalEntries} total entries)
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={!pagination.hasPrevPage || loading}
                          className={`p-1 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400'}`}
                        >
                          <FaArrowLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={!pagination.hasNextPage || loading}
                          className={`p-1 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400'}`}
                        >
                          <FaArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Page Size Selector */}
        {dayBookData.length > 0 && (
          <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Entries per page:</label>
                <select
                  value={filters.limit}
                  onChange={(e) => {
                    handleFilterChange('limit', parseInt(e.target.value));
                    fetchDayBookData(1);
                  }}
                  className={`px-3 py-1 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'}`}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
              
              <div className="flex items-center gap-4">
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sort by:</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => {
                    handleFilterChange('sortBy', e.target.value);
                    fetchDayBookData(1);
                  }}
                  className={`px-3 py-1 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'}`}
                >
                  <option value="date">Date</option>
                  <option value="id">ID</option>
                </select>
                <button
                  onClick={() => {
                    const newOrder = filters.sortOrder === 'asc' ? 'desc' : 'asc';
                    handleFilterChange('sortOrder', newOrder);
                    fetchDayBookData(1);
                  }}
                  className={`px-3 py-1 border rounded-lg text-sm flex items-center gap-1 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  {filters.sortOrder === 'asc' ? '↑' : '↓'} 
                  {filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {Object.keys(groupedData).length > 0 ? (
          <div className={`rounded-2xl shadow-xl border-2 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Day Book Report ({formatDate(filters.dateFrom)} to {formatDate(filters.dateTo)})
                </h2>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Total Entries: {dayBookData.length}
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              {Object.entries(groupedData)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([dateKey, entries]) => (
                <div key={dateKey} className={`border-b last:border-b-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  {/* Date Header */}
                  <div className={`px-6 py-3 border-b ${darkMode ? 'bg-blue-900/30 border-gray-700' : 'bg-blue-50 border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                      {formatDate(dateKey)}
                    </h3>
                  </div>

                  {/* Entries for this date */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Tile Details
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Location
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Supplier
                          </th>
                          <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            ID
                          </th>
                          <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${darkMode ? 'bg-green-900/30 text-gray-400' : 'text-gray-500 bg-green-50'}`}>
                            <div className="flex items-center justify-center gap-1">
                              <FaArrowRight className="text-green-600" />
                              Inward
                            </div>
                          </th>
                          <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${darkMode ? 'bg-red-900/30 text-gray-400' : 'text-gray-500 bg-red-50'}`}>
                            <div className="flex items-center justify-center gap-1">
                              <FaArrowLeft className="text-red-600" />
                              Outward
                            </div>
                          </th>
                        </tr>
                        <tr className={`border-t ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50'}`}>
                          <th className="px-4 py-2"></th>
                          <th className="px-4 py-2"></th>
                          <th className="px-4 py-2"></th>
                          <th className="px-4 py-2"></th>
                          <th className={`px-4 py-2 ${darkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                            <div className={`grid grid-cols-4 gap-1 text-xs ${darkMode ? 'text-gray-400' : ''}`}>
                              <div>FS Box</div>
                              <div>FS Pieces</div>
                              <div>DS Box</div>
                              <div>DS Pieces</div>
                            </div>
                          </th>
                          <th className={`px-4 py-2 ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                            <div className={`grid grid-cols-4 gap-1 text-xs ${darkMode ? 'text-gray-400' : ''}`}>
                              <div>FS Box</div>
                              <div>FS Pieces</div>
                              <div>DS Box</div>
                              <div>DS Pieces</div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                        {entries.map((entry, index) => (
                          <tr key={`${dateKey}_${index}`} className={entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'bg-orange-900/20 hover:bg-orange-900/30' : 'bg-orange-50 hover:bg-orange-100') : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}>
                            <td className="px-4 py-3">
                              <div>
                                <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{entry.tileName}</div>
                                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{entry.tileCode}</div>
                                {entry.outward?.type === 'DEMOLISHED' && (
                                  <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-100 text-orange-700'}`}>
                                    <FaTrashAlt className="w-3 h-3" /> DEMOLISHED
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FaMapMarkerAlt className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                  {entry.location?.name || entry.location?.locationName || 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              {entry.inward?.supplier || 'N/A'}
                            </td>
                            <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                              {entry.inward?.id || entry.outward?.id || 'N/A'}
                            </td>
                            <td className={`px-4 py-3 ${darkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                              <div className="grid grid-cols-4 gap-1 text-sm text-center">
                                <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>{entry.inward?.fsBox || 0}</div>
                                <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>{entry.inward?.fsPieces || 0}</div>
                                <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>{entry.inward?.dsBox || 0}</div>
                                <div className={`font-medium ${darkMode ? 'text-green-400' : 'text-green-700'}`}>{entry.inward?.dsPieces || 0}</div>
                              </div>
                            </td>
                            <td className={`px-4 py-3 ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'bg-orange-900/20' : 'bg-orange-50') : (darkMode ? 'bg-red-900/20' : 'bg-red-50')}`}>
                              <div className="grid grid-cols-4 gap-1 text-sm text-center">
                                <div className={`font-medium ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>{entry.outward?.fsBox || 0}</div>
                                <div className={`font-medium ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>{entry.outward?.fsPieces || 0}</div>
                                <div className={`font-medium ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>{entry.outward?.dsBox || 0}</div>
                                <div className={`font-medium ${entry.outward?.type === 'DEMOLISHED' ? (darkMode ? 'text-orange-400' : 'text-orange-700') : (darkMode ? 'text-red-400' : 'text-red-700')}`}>{entry.outward?.dsPieces || 0}</div>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {/* Show transfers if any */}
                        {entries.some(entry => entry.transfers?.length > 0) && (
                          entries.map((entry, entryIndex) => 
                            entry.transfers?.map((transfer, transferIndex) => (
                              <tr key={`${dateKey}_${entryIndex}_transfer_${transferIndex}`} className={darkMode ? 'bg-yellow-900/20 hover:bg-yellow-900/30' : 'bg-yellow-50 hover:bg-yellow-100'}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <FaTruck className="text-yellow-600" />
                                    <div>
                                      <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{entry.tileName}</div>
                                      <div className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>Transfer {transfer.type}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                    {entry.location?.name || entry.location?.locationName || 'N/A'}
                                  </div>
                                </td>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                  {transfer.type === 'IN' ? `From: ${transfer.fromLocation}` : `To: ${transfer.toLocation}`}
                                </td>
                                <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                  {transfer.remarks || 'N/A'}
                                </td>
                                <td className={`px-4 py-3 ${darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
                                  {transfer.type === 'IN' && (
                                    <div className="grid grid-cols-4 gap-1 text-sm text-center">
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.fsBox || 0}</div>
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.fsPieces || 0}</div>
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.dsBox || 0}</div>
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.dsPieces || 0}</div>
                                    </div>
                                  )}
                                </td>
                                <td className={`px-4 py-3 ${darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
                                  {transfer.type === 'OUT' && (
                                    <div className="grid grid-cols-4 gap-1 text-sm text-center">
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.fsBox || 0}</div>
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.fsPieces || 0}</div>
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.dsBox || 0}</div>
                                      <div className={`font-medium ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{transfer.dsPieces || 0}</div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !loading && (
          <div className={`rounded-2xl shadow-xl border-2 p-12 text-center transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <FaWarehouse className={`mx-auto h-12 w-12 mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No Data Found</h3>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              {filters.dateFrom && filters.dateTo 
                ? 'No tiles transactions found for the selected criteria.'
                : 'Please select date range and click Generate to view the day book report.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
